import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import CustomDropdown from '../CustomDropdown';
import Pagination from '../Pagination';
import ProgressBar from '../ProgressBar';
import ExportButton from '../ExportButton';
import Button from '../Button';
import { BatteryData } from '../../api/service/BatteryInterfaces';
import generateStuffTab1PDF from './StuffTab1PDF';
import { useNavigate } from 'react-router-dom';
import { StuffPageUrl } from '../../url/poenUrl';
import LoadingOverlay from '../LoadingOverlay';
import BuyerBatteryService from '../../api/service/buyer/BuyerBatteryService';
import InquiryPopup from './InquiryPopup';

const Container = styled.div``;

const Header = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 20px;
  @media (max-width: 968px) {
    align-items: start;
  }
`;
const DropdownGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  @media (max-width: 968px) {
    flex-direction: column;
  }
`;

const DropdownGroupRow = styled.div`
  display: flex;
  gap: 10px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 968px) {
    flex-direction: column-reverse;
  }
`;

const TableContainer = styled.div`
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};
`;

const TableBox = styled.div`
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};
  overflow-x: auto;

  @media (max-width: 768px) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: white;

  tr {
    border-bottom: 1px solid #ddd;
  }

  th,
  td {
    font-size: 16px;
    padding: 10px;
    text-align: center;
    vertical-align: middle; /* 세로 정렬 */
    white-space: nowrap; /* 줄바꿈 방지 */
  }

  th {
    background-color: #85c1a9;
    color: white;
    font-weight: 500;
  }

  th:first-child {
    border-top-left-radius: 10px;
  }

  th:last-child {
    border-top-right-radius: 10px;
  }

  @media (max-width: 768px) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;

    tr {
      border-bottom: 1px solid #ddd;
    }

    th,
    td {
      font-size: 12px;
      padding: 10px;
      text-align: center;
      vertical-align: middle; /* 세로 정렬 */
      white-space: nowrap; /* 줄바꿈 방지 */
    }

    th {
      font-weight: 400;
    }

    th:first-child {
      border-top-left-radius: 0px;
    }

    th:last-child {
      border-top-right-radius: 0px;
    }
  }
`;

const Button2 = styled.button`
  background-color: ${(props) => props.theme.colors.sub4Color};
  color: ${(props) => props.theme.colors.whiteColor};
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
`;

function StuffTab1() {
  const navigate = useNavigate(); // navigate 훅 추가
  const [isPDFLoading, setIsPDFLoading] = useState(false); // 로딩 상태 추가

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1); // 총 페이지 수
  const [tableData, setTableData] = useState<BatteryData[]>([]); // BatteryData 타입 사용

  const [manufacturerOptions, setManufacturerOptions] = useState<
    { key: string; value: string; label: string }[]
  >([]); // 제조사 옵션 상태
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>(''); // 제조사

  const [modelOptions, setModelOptions] = useState<
    { key: string; value: string; label: string }[]
  >([]); // 차종 옵션
  const [selectedModel, setSelectedModel] = useState<string>(''); // 선택된 차종

  const [selectedSortOrder, setSelectedSortOrder] = useState<string>(''); // 정렬 기준 상태

  const [selectedPriceRange, setSelectedPriceRange] = useState<string>(''); // 선택된 판매 금액 범위
  //문의하기 팝업
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BatteryData | null>(null); // 선택된 item 관리

  // 판매 금액 옵션
  const priceRangeOptions = [
    { key: '', value: '', label: '판매금액' }, // 전체 옵션 추가
    { key: 'below500', value: 'below500', label: '500만원 미만' },
    { key: '500to1000', value: '500to1000', label: '500~1,000만원' },
    { key: 'above1000', value: 'above1000', label: '1,000만원 초과' },
  ];

  // 검색 버튼 클릭 시
  const handleSearch = () => {
    fetchBatteryList(currentPage); // 다른 조건들 기반으로 검색
  };

  const handlePageChange = (page: number) => {
    // console.log('페이지 변경:', page + 1);
    setCurrentPage(page);
    fetchBatteryList(page); // 페이지 변경 시 데이터 로드
  };

  // 배터리 리스트 API 호출
  const fetchBatteryList = async (page: number = 0) => {
    try {
      let minPrice, maxPrice;

      // 선택된 가격 범위에 따라 minPrice와 maxPrice 설정
      switch (selectedPriceRange) {
        case 'below500':
          minPrice = 0;
          maxPrice = 500;
          break;
        case '500to1000':
          minPrice = 500;
          maxPrice = 1000;
          break;
        case 'above1000':
          minPrice = 1001; // 1,000만원 초과
          maxPrice = undefined; // 상한 없음
          break;
        default:
          minPrice = undefined;
          maxPrice = undefined;
          break;
      }

      // 로컬 스토리지에서 permissions 가져오기
      const permissions = localStorage.getItem('permissions');

      const params = {
        page: page,
        size: 10,
        carManufacturerId: selectedManufacturer || undefined,
        carModelId: selectedModel || undefined,
        sortBy: selectedSortOrder as 'asc' | 'desc' | undefined, // 올바른 타입으로 변환
        minPrice, // 최소 가격
        maxPrice, // 최대 가격
        ...(permissions === 'ADMIN' && { filter: 'NOT_REGISTERED' }), // ADMIN일 때만 filter 추가
      };

      const response = await BuyerBatteryService.getBatteryList(params); // API 호출
      setTableData(response.data.content || []); // 데이터 저장
      setTotalPages(response.data.totalPages); // 총 페이지 수 저장
    } catch (error) {
      console.error('배터리 리스트 조회 중 오류:', error);
    }
  };

  const fetchAllBatteryList = async (): Promise<BatteryData[]> => {
    try {
      let allData: BatteryData[] = []; // 타입 명시
      let page = 0;
      let totalPages = 1;

      let minPrice, maxPrice;

      // 선택된 가격 범위에 따라 minPrice와 maxPrice 설정
      switch (selectedPriceRange) {
        case 'below500':
          minPrice = 0;
          maxPrice = 500;
          break;
        case '500to1000':
          minPrice = 500;
          maxPrice = 1000;
          break;
        case 'above1000':
          minPrice = 1001; // 1,000만원 초과
          maxPrice = undefined; // 상한 없음
          break;
        default:
          minPrice = undefined;
          maxPrice = undefined;
          break;
      }

      while (page < totalPages) {
        const response = await BuyerBatteryService.getBatteryList({
          page,
          size: 1000,
          carManufacturerId: selectedManufacturer || undefined,
          carModelId: selectedModel || undefined,
          sortBy: selectedSortOrder as 'asc' | 'desc' | undefined, // 타입 캐스팅 추가
          minPrice, // 최소 가격
          maxPrice, // 최대 가격
        });

        allData = [...allData, ...response.data.content];
        totalPages = response.data.totalPages;
        page++;
      }

      return allData;
    } catch (error) {
      console.error('전체 데이터 조회 중 오류:', error);
      return [];
    }
  };

  // 제조사 목록을 가져오는 함수
  const fetchManufacturers = async () => {
    try {
      const response = await BuyerBatteryService.getBatteryManufacturers(); // 제조사 목록 API 호출
      const manufacturers = response.data.map((manufacturer) => ({
        key: manufacturer.carManufacturerId, // 고유 ID
        value: manufacturer.carManufacturerId, // 값
        label: manufacturer.carManufacturerName, // 표시 이름
      }));

      // 기본 옵션과 API 데이터를 합치기
      setManufacturerOptions([
        { key: '', value: '', label: '제조사' }, // 기본 옵션
        ...manufacturers, // API에서 가져온 옵션
      ]);
    } catch (error) {
      console.error('제조사 목록 조회 중 오류:', error);
    }
  };

  // 선택된 제조사에 따른 차종 목록 가져오기
  const fetchModels = async (manufacturerId: string) => {
    try {
      if (!manufacturerId) {
        setModelOptions([]); // 제조사가 선택되지 않으면 차종 옵션 초기화
        return;
      }

      const response =
        await BuyerBatteryService.getManufacturerModels(manufacturerId);
      const models = response.data.map((model) => ({
        key: model.carModelId,
        value: model.carModelId,
        label: model.carModelName,
      }));
      setModelOptions([
        { key: '', value: '', label: '차종' }, // 기본 옵션
        ...models,
      ]);
    } catch (error) {
      console.error('차종 목록 조회 중 오류:', error);
    }
  };

  useEffect(() => {
    // 처음 로드 시 제조사 목록 및 배터리 리스트 가져오기
    sessionStorage.removeItem('StuffbatteryId');
    fetchManufacturers();
    fetchBatteryList(currentPage);
  }, []);

  useEffect(() => {
    // tableData가 업데이트되면 첫 번째 항목의 batteryId를 sessionStorage에 저장
    if (tableData.length > 0) {
      const firstBatteryId = tableData[0].batteryId;

      // 만약 sessionStorage에 이미 값이 저장되어 있다면 덮어쓰지 않음
      const existingBatteryId = sessionStorage.getItem('StuffbatteryId');
      if (!existingBatteryId) {
        sessionStorage.setItem('StuffbatteryId', firstBatteryId);
      }
    }
  }, [tableData]);

  useEffect(() => {
    // 제조사가 변경될 때 차종 옵션 초기화 및 데이터 다시 불러오기
    setSelectedModel(''); // 차종 선택 초기화
    fetchModels(selectedManufacturer); // 선택된 제조사에 따라 차종 목록 업데이트
  }, [selectedManufacturer]);

  const handleDownloadPDF = async () => {
    try {
      setIsPDFLoading(true); // 로딩 시작
      const allTableData = await fetchAllBatteryList();
      const pdfBytes = await generateStuffTab1PDF(allTableData); // 테이블 데이터를 PDF로 변환
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '매물정보_매물리스트.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('PDF 생성 중 오류 발생:', error);
    } finally {
      setIsPDFLoading(false); // 로딩 종료
    }
  };
  //페이지 이동
  const handleRowClick = (batteryId: string) => {
    sessionStorage.setItem('StuffbatteryId', batteryId);
    navigate(`${StuffPageUrl}?activeTab=1`); // DiagnosisTab2로 이동
  };

  const handleInquiryClick = (item: BatteryData) => {
    setSelectedItem(item); // 선택된 item 저장
    setIsPopupOpen(true); // 팝업 열기
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setSelectedItem(null); // 팝업 닫힐 때 item 초기화
  };
  return (
    <Container>
      {isPDFLoading && <LoadingOverlay />}
      <Header>
        <DropdownGroup>
          <DropdownGroupRow>
            <CustomDropdown
              options={manufacturerOptions}
              selected={selectedManufacturer}
              onSelect={setSelectedManufacturer}
              placeholder={'제조사'}
            />
            <CustomDropdown
              options={modelOptions}
              selected={selectedModel}
              onSelect={setSelectedModel}
              placeholder={'차종'}
            />
          </DropdownGroupRow>
          <DropdownGroupRow>
            <CustomDropdown
              options={[
                { key: 'desc', value: 'desc', label: '최신순' },
                { key: 'asc', value: 'asc', label: '오래된순' },
              ]}
              selected={selectedSortOrder} // 새로운 상태로 선택된 옵션 관리
              onSelect={setSelectedSortOrder} // 상태 업데이트
              placeholder={'측정일'}
            />
            <CustomDropdown
              options={priceRangeOptions}
              selected={selectedPriceRange}
              onSelect={setSelectedPriceRange} // 선택된 가격 범위 업데이트
              placeholder={'판매금액'}
            />
          </DropdownGroupRow>
        </DropdownGroup>
        <ButtonContainer>
          <Button save={true} onClick={handleSearch}>
            검색
          </Button>
          <ExportButton onClick={handleDownloadPDF} />
        </ButtonContainer>
      </Header>
      {/* <DiagnosisTab1HiddenPDFContainer ref={pdfRef} tableData={tableData} /> */}
      <TableContainer id="table-container">
        <TableBox>
          <Table>
            <thead>
              <tr>
                <th>NO</th>
                <th>매물상태</th>
                <th>회사명</th>
                <th>제조사</th>
                <th>차량</th>
                <th>SOH</th>
                <th>SOC</th>
                <th>셀편차</th>
                <th>고장코드</th>
                <th>측정일</th>
                <th>판매금액</th>
                <th>문의하기</th>
              </tr>
            </thead>
            <tbody>
              {tableData.length === 0 && (
                <tr>
                  <td
                    colSpan={13}
                    style={{ textAlign: 'center', padding: '20px' }}
                  >
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
              {tableData.map((item, index) => (
                <tr
                  key={item.batteryId}
                  onClick={() => handleRowClick(item.batteryId)}
                >
                  <td>{index + 1}</td>
                  <td>
                    <span
                      style={{
                        color:
                          item.saleStatus === '판매예약'
                            ? '#0083D4'
                            : item.saleStatus === '판매완료'
                              ? '#D40000'
                              : '#000',
                      }}
                    >
                      {item.saleStatus}
                    </span>
                  </td>
                  <td>{item.company}</td>
                  <td>{item.manufacturer}</td>
                  <td>{item.model}</td>
                  {/* SOH와 SOC에서 ProgressBar 컴포넌트 사용 */}
                  <td>
                    <ProgressBar percentage={item.soh} type="SOH" />
                  </td>
                  <td>
                    <ProgressBar percentage={item.soc} type="SOC" />
                  </td>
                  <td>{item.cellDeviation}</td>
                  <td>{item.failureCodeYN}</td>
                  <td>{item.measurementDate}</td>
                  <td>{item.price ? `${item.price.toLocaleString()}` : '-'}</td>
                  <td
                    onClick={(e) => e.stopPropagation()} // <td> 레벨에서 이벤트 차단
                  >
                    <Button2 onClick={() => handleInquiryClick(item)}>
                      문의하기
                    </Button2>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableBox>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </TableContainer>

      {isPopupOpen && (
        <InquiryPopup
          onClose={handlePopupClose}
          item={selectedItem} // 선택된 item 전달
        />
      )}
    </Container>
  );
}

export default StuffTab1;
